import { AuthService } from '@apparel/core/auth/AuthService';
import { Log } from '@apparel/core/log';
import { checkUserDomain } from '@apparel/core/utils';
import { PLATFORM_BASE } from '@constants/Defaults';
import { api } from '@lib/http';
import { clearCustomer, clearToken, clearUser, setCustomer, setToken, setUser } from '@lib/jwt';
import { t } from '@lib/translation';
import { Customer } from '@model/Customer';
import { User } from '@model/User';
import { app } from '@packages/app';
import { call } from '@packages/functions';
import { ensureFirstSlash } from '@packages/http/utils';
import { RouterServiceProvider } from '@packages/router/lib/RouterServiceProvider';
import { setAlert } from '@redux/Alerts/actions';
import { notifySetAlert } from '@redux/NotificationAlert/actions';
// @ts-ignore
import { all, fork, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { changeLanguage } from '../Internalization/actions';
import { loadedAction, loadingAction } from '../Page/actions';
import { loginLoading, loginSuccess, logoutSuccess } from './actions';
import * as TYPES from './types';

const log = Log.channel('Saga@Auth');
let auth: AuthService;

export function* loginRequest({ notify }: any): any {
    yield takeEvery(TYPES.AUTH_LOGIN_REQUEST, function* (action: any) {
        if (!action.payload) {
            return;
        }

        const { u, after, onError, token, user: userObject, ...payload } = action.payload;
        const router$ = app<RouterServiceProvider>(RouterServiceProvider);

        yield put(loginLoading(true));
        yield put(loadingAction());

        try {
            const user = new User();

            // noinspection JSUnusedAssignment
            let res$: any = {};

            if (!token) {
                res$ = yield api
                    .post('/authenticate', {
                        ...payload,
                        with_user: true,
                        apparel_platform_user: true,
                        fields: user.$fields(),
                    })
                    .then(res$);

                const userView = res$?.data?.user?.user_view;

                if (userView === 'admin' || userView === 'salesman') {
                    const alert$ = yield api.get('/alerts', {
                        headers: {
                            Authorization: 'Bearer ' + res$.data.token,
                        },
                    });
                    yield put(setAlert('unread_redo_request_count', alert$.data.unread_redo_request_count));
                }
                if (
                    userView === 'admin' ||
                    userView === 'salesman' ||
                    userView === 'designer' ||
                    userView === 'wholesaler'
                ) {
                    const notifyAlert$ = yield api.get('/apparel-notifications', {
                        headers: {
                            Authorization: 'Bearer ' + res$.data.token,
                        },
                        params: {
                            return_only: 'id',
                            limit: 1,
                        },
                    });
                    yield put(notifySetAlert('unread_notification_count', notifyAlert$.data?.unread));
                    yield put(notifySetAlert('notification_count', notifyAlert$.data?.total));
                }

                // redirecting to correct platform
                checkUserDomain(res$.data);
            } else {
                res$ = {
                    data: {
                        user: userObject,
                        token,
                        customer: null,
                    },
                };
            }

            // Store auth state
            setUser(res$.data.user);
            setToken(res$.data.token);

            yield auth.authenticateWithToken(res$.data.token);
            res$.data.user = user.$fill(res$.data.user);

            if (res$.data.user.$is('customer') && res$.data.user.customer_id > 0) {
                // Fetch customer
                const { data } = yield Customer.find(res$.data.user.customer_id);
                res$.data.customer = data;
                setCustomer(data);
            }

            yield put(
                loginSuccess({
                    ...res$,
                    _identifier: action.payload.identifier,
                }),
            );

            yield put(changeLanguage(user.get('language.code')));

            yield call(after, res$.data.user);
            yield put(loadedAction());

            // redirect to first access url (before sign-in page)
            if (u) {
                if (/^(\^|http)/.test(u)) {
                    if (typeof window !== 'undefined') {
                        window.location.href = u;
                    }
                } else {
                    let redirectUrl = ensureFirstSlash(u);

                    if (redirectUrl.startsWith(PLATFORM_BASE)) {
                        redirectUrl = redirectUrl.replace(PLATFORM_BASE, '');
                    }

                    router$.replace(ensureFirstSlash(redirectUrl));
                }
            }
        } catch (error) {
            log.error('loginRequest.error', { error });

            const { response } = error;
            let skip = false;
            let data$ = error;

            const { data, status } = (response || {
                data: { error: error?.message ?? 'Something went wrong!' },
                status: null,
            }) as any;

            if (data.type === 'AlreadyLoggedError') {
                skip = true;
                router$.goTo('/');
            } else if (data.detail || data.error) {
                const err = data.detail || data.error;

                // eslint-disable-next-line default-case
                switch (true) {
                    case err === 'invalid_credentials':
                        data.error = `${t('Invalid Credentials')}`;
                        break;
                    case status === -1:
                        data.error = `${t('No network connection')}`;
                        break;
                    case status === 500:
                        data.error = `${t('Server connection error')}`;
                        break;
                }

                data$ = data;
            }

            if (!skip) {
                notify.error(data$);
            }

            yield put(loginLoading(false));
            yield auth.cleanup();
            yield call(onError, error);
        }

        yield put(loadedAction());
    });
}

export function* checkRequest({ notify }: any): any {
    yield takeEvery(TYPES.AUTH_LOGIN_CHECK, function* () {
        yield put(loginLoading(true));

        try {
            const loggedUser = yield auth.checkAuthentication();
            const userView = loggedUser?.user_view;

            const token = localStorage.getItem('__token__');
            if (userView === 'admin' || userView === 'salesman') {
                if (token) {
                    const alert$ = yield api.get('/alerts', {
                        headers: {
                            Authorization: 'Bearer ' + token,
                        },
                    });

                    yield put(setAlert('unread_redo_request_count', alert$.data.unread_redo_request_count));
                }
            }
            if (
                userView === 'admin' ||
                userView === 'salesman' ||
                userView === 'designer' ||
                userView === 'wholesaler'
            ) {
                const notifyAlert$ = yield api.get('/apparel-notifications', {
                    headers: {
                        Authorization: 'Bearer ' + token,
                    },
                    params: {
                        return_only: 'id',
                        limit: 1,
                    },
                });
                yield put(notifySetAlert('unread_notification_count', notifyAlert$.data?.unread));
                yield put(notifySetAlert('notification_count', notifyAlert$.data?.total));
            }

            const user = new User(loggedUser);
            const customer = new Customer(loggedUser?.customer);

            if (loggedUser) {
                yield put(loginSuccess({ user, customer }));
                yield put(changeLanguage(loggedUser.language_code));
            }
        } catch (error) {
            log.error('checkRequest.error', { error });
            notify.error(error);
            // yield put(executeLogout());
        }

        yield put(loginLoading(false));
    });
}

export function* logoutRequest() {
    yield takeLatest(TYPES.AUTH_LOGOUT_REQUEST, function* () {
        // TODO: fully replace with @apparel/core/auth
        clearUser();
        clearCustomer();
        clearToken();
        try {
            yield api.get('/logout', { params: { nored: 1 } });
        } catch (e) {}
        // end

        yield auth.logout();
        yield put(logoutSuccess());
    });
}

export default function* AuthSaga(services: any): any {
    auth = app(AuthService);

    yield all([fork(loginRequest, services), fork(checkRequest, services), fork(logoutRequest as any, services)]);
}
